'use client'

import { memo, useRef } from 'react'
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
  type MotionValue,
} from 'framer-motion'
import { CRAFT_MEDIA } from './craft-media'

/** baby: Full craft chips orbit the core, not squished into 14px tag icons. */
const SATELLITES = [
  {
    id: 'weights',
    label: 'On-device model',
    src: CRAFT_MEDIA.adam.modelWeights,
    className: 'craft-adam__sat--weights',
  },
  {
    id: 'device',
    label: 'Local inference',
    src: CRAFT_MEDIA.adam.modelDevice,
    className: 'craft-adam__sat--device',
  },
  {
    id: 'agent',
    label: 'Private agent',
    src: CRAFT_MEDIA.adam.modelAgent,
    className: 'craft-adam__sat--agent',
  },
] as const

type CraftHeroAdamProps = {
  /** baby: Optional progress override for tests / Storybook. */
  progress?: MotionValue<number> | null
}

/**
 * baby: Creation-of-Adam craft scroll: hands reach model core; satellite chips settle in.
 * All adam/*.png cutouts are used (core + weights + device + agent).
 */
export const CraftHeroAdam = memo(function CraftHeroAdam({
  progress: progressOverride = null,
}: CraftHeroAdamProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const reduceMotion = useReducedMotion()

  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ['start end', 'end start'],
  })

  const progress = progressOverride ?? scrollYProgress

  // baby: Hands start wide, settle toward the model mid-scroll.
  const leftX = useTransform(progress, [0.05, 0.55], ['-26%', '0%'])
  const rightX = useTransform(progress, [0.05, 0.55], ['26%', '0%'])
  const coreScale = useTransform(progress, [0.1, 0.55], [0.78, 1])
  const coreOpacity = useTransform(progress, [0.05, 0.35], [0.25, 1])
  const captionOpacity = useTransform(progress, [0.15, 0.45], [0, 1])
  const captionY = useTransform(progress, [0.15, 0.45], [10, 0])
  const satOpacity = useTransform(progress, [0.32, 0.62], [0, 1])
  const satScale = useTransform(progress, [0.32, 0.62], [0.72, 1])

  return (
    <div
      ref={trackRef}
      className="craft-adam"
      data-testid="craft-hero-adam"
      aria-label="On-device AI model scroll scene"
    >
      <div className="craft-adam__sticky">
        <motion.p
          className="craft-adam__caption"
          style={
            reduceMotion
              ? undefined
              : { opacity: captionOpacity, y: captionY }
          }
        >
          <span className="craft-adam__caption-strong">On-device AI</span>{' '}
          <span className="craft-adam__caption-soft">models &amp; agents</span>
        </motion.p>

        <div className="craft-adam__stage">
          <motion.img
            className="craft-adam__hand craft-adam__hand--left"
            src={CRAFT_MEDIA.adam.handLeft}
            alt=""
            aria-hidden
            draggable={false}
            style={reduceMotion ? undefined : { x: leftX }}
          />

          <div className="craft-adam__core-wrap">
            {/* baby: Satellite craft chips: the other generated PNGs, full size. */}
            <motion.ul
              className="craft-adam__sats"
              style={
                reduceMotion
                  ? undefined
                  : { opacity: satOpacity, scale: satScale }
              }
            >
              {SATELLITES.map((sat) => (
                <li key={sat.id} className={`craft-adam__sat ${sat.className}`}>
                  <img
                    src={sat.src}
                    alt=""
                    aria-hidden
                    draggable={false}
                    className="craft-adam__sat-img"
                  />
                  <span className="craft-adam__sat-label">{sat.label}</span>
                </li>
              ))}
            </motion.ul>

            <motion.div
              className="craft-adam__core"
              style={
                reduceMotion
                  ? undefined
                  : { scale: coreScale, opacity: coreOpacity }
              }
            >
              <img
                className="craft-adam__model"
                src={CRAFT_MEDIA.adam.modelCore}
                alt=""
                aria-hidden
                draggable={false}
              />
              <span className="craft-adam__label">Local model</span>
            </motion.div>
          </div>

          <motion.img
            className="craft-adam__hand craft-adam__hand--right"
            src={CRAFT_MEDIA.adam.handRight}
            alt=""
            aria-hidden
            draggable={false}
            style={reduceMotion ? undefined : { x: rightX }}
          />
        </div>
      </div>
    </div>
  )
})

CraftHeroAdam.displayName = 'CraftHeroAdam'
